import React, { useState, useEffect, useCallback, useRef } from "react";
import AddPlayerForm from "../../components/AddPlayerForm/AddPlayerForm";
import AddPlayerFormArg from "../../components/AddPlayerForm/AddPlayerFormArg";
import EditPlayerForm from "../../components/EditPlayerForm/EditPlayerForm";
import EditPlayerFormArg from "../../components/EditPlayerForm/EditPlayerFormArg";
import ManageFields from "../../components/ManageFields/ManageFields";
import CotizacionesPanel from "../../components/CotizacionesPanel/CotizacionesPanel";
import "./AdminPage.css";

const API = process.env.REACT_APP_BACKEND_URL || "https://api.footy-games.com.ar";

const adminFetch = async (path, options = {}) => {
    const raw = localStorage.getItem("authUserData");
    const token = raw ? JSON.parse(raw).token : null;
    const res = await fetch(`${API}${path}`, {
        ...options,
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            ...(options.headers || {}),
        },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Error");
    return data;
};

// ─── VERSION PANEL ────────────────────────────────────────────────────────────
const VersionPanel = () => {
    const [versions, setVersions] = useState({ ios: "—", android: "—" });
    const [iosInput, setIosInput] = useState("");
    const [androidInput, setAndroidInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState(null);

    const fetchVersions = useCallback(async () => {
        try {
            const data = await adminFetch("/app/min-version");
            setVersions(data.minVersions);
        } catch (e) {
            setMsg({ type: "error", text: e.message });
        }
    }, []);

    useEffect(() => { fetchVersions(); }, [fetchVersions]);

    const handleSave = async () => {
        if (!iosInput && !androidInput) {
            setMsg({ type: "error", text: "Ingresá al menos una versión." });
            return;
        }
        setLoading(true);
        setMsg(null);
        try {
            const body = {};
            if (iosInput) body.ios = iosInput;
            if (androidInput) body.android = androidInput;
            const data = await adminFetch("/app/set-min-version", {
                method: "POST",
                body: JSON.stringify(body),
            });
            setVersions(data.minVersions);
            setIosInput("");
            setAndroidInput("");
            setMsg({ type: "success", text: "Versiones actualizadas y persistidas en MongoDB ✓" });
        } catch (e) {
            setMsg({ type: "error", text: e.message });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="adm-panel">
            <h2 className="adm-panel-title">Control de Versiones</h2>
            <p className="adm-panel-desc">
                Cuando un usuario abre la app, se compara su versión con la mínima requerida.
                Si está por debajo, se muestra una pantalla de actualización obligatoria.
                Los valores se persisten en MongoDB y sobreviven reinicios del servidor.
            </p>

            <div className="adm-version-current">
                <div className="adm-version-badge">
                    <span className="adm-version-label">🍎 iOS mínimo</span>
                    <span className="adm-version-value">{versions.ios}</span>
                </div>
                <div className="adm-version-badge">
                    <span className="adm-version-label">🤖 Android mínimo</span>
                    <span className="adm-version-value">{versions.android}</span>
                </div>
            </div>

            <div className="adm-form-row">
                <div className="adm-field">
                    <label>Nueva versión iOS</label>
                    <input
                        value={iosInput}
                        onChange={e => setIosInput(e.target.value)}
                        placeholder="ej: 1.3.0"
                    />
                </div>
                <div className="adm-field">
                    <label>Nueva versión Android</label>
                    <input
                        value={androidInput}
                        onChange={e => setAndroidInput(e.target.value)}
                        placeholder="ej: 1.3.0"
                    />
                </div>
            </div>

            <button className="adm-btn adm-btn-primary" onClick={handleSave} disabled={loading}>
                {loading ? "Guardando…" : "Guardar versiones mínimas"}
            </button>

            {msg && (
                <p className={`adm-msg adm-msg-${msg.type}`}>{msg.text}</p>
            )}
        </div>
    );
};

// ─── CAREER MAINTENANCE PANEL ─────────────────────────────────────────────────
// Editor de valoraciones (estrellas) y ranking FIFA de las selecciones del Mundial, filtrable por
// confederación. Persiste overrides en AppConfig; la carrera los aplica al cargar.
const CONF_LABEL = { UEFA: 'UEFA (Europa)', Asia: 'Asia', Africa: 'África', CONCACAF: 'CONCACAF', Conmebol: 'Conmebol (Sudamérica)', Oceania: 'Oceanía' };
const ESTRELLAS_OPC = [5, 4.5, 4, 3.5, 3, 2.5, 2, 1.5, 1, 0.5];
const SeleccionesPanel = () => {
    const [selecciones, setSelecciones] = useState(null);
    const [edits, setEdits] = useState({}); // { pais: {estrellas, ranking} } cambios sin guardar
    const [conf, setConf] = useState("UEFA");
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState(null);

    const load = useCallback(async () => {
        try {
            const data = await adminFetch("/career/selecciones-config");
            setSelecciones(data.selecciones || []);
        } catch (e) { setMsg({ type: "error", text: e.message }); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const setVal = (pais, campo, valor) => {
        setEdits((prev) => ({ ...prev, [pais]: { ...prev[pais], [campo]: valor } }));
    };
    const valEfectivo = (s, campo) => (edits[s.pais]?.[campo] ?? s[campo]);

    const guardar = async () => {
        setLoading(true); setMsg(null);
        try {
            // Construir overrides: para cada país, el valor efectivo (editado o actual) vs base.
            const overrides = {};
            for (const s of selecciones) {
                const est = edits[s.pais]?.estrellas ?? s.estrellas;
                const rk = edits[s.pais]?.ranking ?? s.ranking;
                if (est !== s.estrellas_base || rk !== s.ranking_base) {
                    overrides[s.pais] = { estrellas: Number(est), ranking: Number(rk) };
                }
            }
            await adminFetch("/career/selecciones-config", { method: "POST", body: JSON.stringify({ overrides }) });
            setEdits({});
            await load();
            setMsg({ type: "success", text: `Guardado. ${Object.keys(overrides).length} selección(es) con override.` });
        } catch (e) { setMsg({ type: "error", text: e.message }); }
        finally { setLoading(false); }
    };

    const lista = (selecciones || []).filter((s) => s.confederacion === conf).sort((a, b) => valEfectivo(a, 'ranking') - valEfectivo(b, 'ranking'));
    const hayCambios = Object.keys(edits).length > 0;

    return (
        <div className="adm-panel">
            <h2 className="adm-panel-title">Selecciones (Mundial)</h2>
            <p className="adm-panel-desc">
                Editá la valoración (estrellas) y el ranking FIFA de cada selección. Se guarda en MongoDB
                y la carrera lo aplica al cargar (afecta los próximos sorteos/ciclos). Filtrá por confederación.
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                {Object.keys(CONF_LABEL).map((c) => (
                    <button key={c} className={`adm-tab ${conf === c ? "adm-tab--active" : ""}`} onClick={() => setConf(c)}>{CONF_LABEL[c]}</button>
                ))}
            </div>
            {!selecciones && <p>Cargando…</p>}
            {selecciones && (
                <div style={{ overflowX: "auto" }}>
                    <table className="adm-table" style={{ width: "100%", fontSize: 13 }}>
                        <thead><tr><th>País</th><th>Bombo</th><th>Ranking FIFA</th><th>Estrellas</th></tr></thead>
                        <tbody>
                            {lista.map((s) => {
                                const editado = !!edits[s.pais];
                                return (
                                    <tr key={s.pais} style={editado ? { background: "rgba(250,204,21,0.08)" } : undefined}>
                                        <td>{s.nombre}</td>
                                        <td>{s.bombo ?? "—"}</td>
                                        <td>
                                            <input type="number" min="1" max="300" value={valEfectivo(s, "ranking")}
                                                onChange={(e) => setVal(s.pais, "ranking", Number(e.target.value))}
                                                style={{ width: 64 }} />
                                        </td>
                                        <td>
                                            <select value={valEfectivo(s, "estrellas")} onChange={(e) => setVal(s.pais, "estrellas", Number(e.target.value))}>
                                                {ESTRELLAS_OPC.map((v) => <option key={v} value={v}>{v}★</option>)}
                                            </select>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            <div style={{ marginTop: 16 }}>
                <button className="adm-btn adm-btn-primary" onClick={guardar} disabled={loading || !hayCambios}>
                    {loading ? "Guardando…" : hayCambios ? `Guardar cambios (${Object.keys(edits).length})` : "Sin cambios"}
                </button>
            </div>
            {msg && <p style={{ marginTop: 12, color: msg.type === "error" ? "#e05252" : "#4caf50" }}>{msg.text}</p>}
        </div>
    );
};

const CareerMaintenancePanel = () => {
    const [maintenance, setMaintenance] = useState(null); // null = cargando
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState(null);

    const fetchStatus = useCallback(async () => {
        try {
            const data = await adminFetch("/career/maintenance");
            setMaintenance(!!data.maintenance);
        } catch (e) {
            setMsg({ type: "error", text: e.message });
        }
    }, []);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    const toggle = async (value) => {
        setLoading(true);
        setMsg(null);
        try {
            const data = await adminFetch("/career/maintenance", {
                method: "POST",
                body: JSON.stringify({ maintenance: value }),
            });
            setMaintenance(!!data.maintenance);
            setMsg({ type: "success", text: data.maintenance ? "Modo carrera EN MANTENIMIENTO (solo admins juegan)." : "Modo carrera HABILITADO para todos." });
        } catch (e) {
            setMsg({ type: "error", text: e.message });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="adm-panel">
            <h2 className="adm-panel-title">Mantenimiento del Modo Carrera</h2>
            <p className="adm-panel-desc">
                Con el mantenimiento activado, solo los administradores pueden jugar el modo carrera;
                el resto de los usuarios ve un cartel de "en mantenimiento". El estado se guarda en
                MongoDB y aplica en el acto, sin necesidad de deployar.
            </p>

            <div className="adm-version-current">
                <div className="adm-version-badge">
                    <span className="adm-version-label">Estado actual</span>
                    <span className="adm-version-value">
                        {maintenance === null ? "…" : maintenance ? "🔧 En mantenimiento" : "✅ Habilitado"}
                    </span>
                </div>
            </div>

            {maintenance ? (
                <button className="adm-btn adm-btn-primary" onClick={() => toggle(false)} disabled={loading}>
                    {loading ? "Guardando…" : "✅ Habilitar el juego para todos"}
                </button>
            ) : (
                <button className="adm-btn adm-btn-danger" onClick={() => toggle(true)} disabled={loading || maintenance === null}>
                    {loading ? "Guardando…" : "🔧 Poner en mantenimiento (solo admins)"}
                </button>
            )}

            {msg && <p className={`adm-msg adm-msg-${msg.type}`}>{msg.text}</p>}
        </div>
    );
};

// ─── USERS PANEL ──────────────────────────────────────────────────────────────
const UsersPanel = () => {
    const [query, setQuery] = useState("");
    const [users, setUsers] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [msg, setMsg] = useState(null);

    const search = async () => {
        if (!query.trim()) return;
        setLoading(true);
        setMsg(null);
        setSelected(null);
        try {
            const data = await adminFetch(`/user/admin/search?q=${encodeURIComponent(query.trim())}`);
            setUsers(data.users || []);
            if ((data.users || []).length === 0) setMsg({ type: "info", text: "No se encontraron usuarios." });
        } catch (e) {
            setMsg({ type: "error", text: e.message });
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") search();
    };

    const handleResetPremium = async () => {
        if (!selected) return;
        setResetLoading(true);
        setMsg(null);
        try {
            const body = selected.email
                ? { email: selected.email }
                : { username: selected.username };
            const data = await adminFetch("/app/reset-premium", {
                method: "POST",
                body: JSON.stringify(body),
            });
            setMsg({ type: "success", text: `Premium reseteado para ${data.username} ✓` });
            setSelected(prev => prev ? { ...prev, premium: false, adsRemoved: false } : null);
            setUsers(prev => prev.map(u =>
                u._id === selected._id ? { ...u, premium: false, adsRemoved: false } : u
            ));
        } catch (e) {
            setMsg({ type: "error", text: e.message });
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <div className="adm-panel">
            <h2 className="adm-panel-title">Usuarios</h2>
            <p className="adm-panel-desc">Buscá usuarios por nombre o email. Podés ver su estado premium y resetearlo.</p>

            <div className="adm-search-row">
                <input
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Buscar por username o email…"
                />
                <button className="adm-btn adm-btn-primary" onClick={search} disabled={loading}>
                    {loading ? "…" : "Buscar"}
                </button>
            </div>

            {msg && <p className={`adm-msg adm-msg-${msg.type}`}>{msg.text}</p>}

            {users.length > 0 && (
                <div className="adm-user-list">
                    {users.map(u => (
                        <div
                            key={u._id}
                            className={`adm-user-row ${selected?._id === u._id ? "adm-user-row--selected" : ""}`}
                            onClick={() => setSelected(u)}
                        >
                            <span className="adm-user-name">{u.username}</span>
                            <span className="adm-user-email">{u.email}</span>
                            <div className="adm-badges">
                                {u.premium && <span className="adm-badge adm-badge-gold">Premium</span>}
                                {u.adsRemoved && <span className="adm-badge adm-badge-blue">Sin ads</span>}
                                {u.isAdmin && <span className="adm-badge adm-badge-red">Admin</span>}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {selected && (
                <div className="adm-user-detail">
                    <h3>@{selected.username}</h3>
                    <div className="adm-detail-grid">
                        <div><span>Email</span><strong>{selected.email || "—"}</strong></div>
                        <div><span>ID</span><strong className="adm-mono">{selected._id}</strong></div>
                        <div><span>Premium</span><strong>{selected.premium ? "✅ Sí" : "❌ No"}</strong></div>
                        <div><span>Sin anuncios</span><strong>{selected.adsRemoved ? "✅ Sí" : "❌ No"}</strong></div>
                        <div><span>Admin</span><strong>{selected.isAdmin ? "✅ Sí" : "❌ No"}</strong></div>
                        <div><span>Monedas</span><strong>{selected.coins ?? "—"}</strong></div>
                    </div>
                    <button
                        className="adm-btn adm-btn-danger"
                        onClick={handleResetPremium}
                        disabled={resetLoading || (!selected.premium && !selected.adsRemoved)}
                    >
                        {resetLoading ? "Reseteando…" : "Resetear premium / ads"}
                    </button>
                </div>
            )}
        </div>
    );
};

// ─── PLAYERS PANEL ────────────────────────────────────────────────────────────
// Las dos pestañas escriben en colecciones distintas: "World" en `players` y "Argentina" en
// `playerargs`. Es transitorio. `playerargs` se descontinua a favor del campo `pools` de
// `players` ('global' / 'arg'), que el backend deduce de los clubes del jugador.
//
// Los formularios *Arg siguen apuntando a `playerargs` a proposito: el juego argentino todavia
// lee esa coleccion, asi que apuntarlos a `players` ahora haria que se editara una cosa y el
// juego mostrara otra. Cuando el import masivo de la extension llene el pool 'arg' se cambian
// los seams `league === 'arg' ? PlayerArg : Player` del backend y recien ahi se borran
// AddPlayerFormArg / EditPlayerFormArg y esta division en dos pestañas.
const PlayersPanel = ({ league }) => {
    const [tab, setTab] = useState("add");
    const isArg = league === "arg";

    return (
        <div className="adm-panel">
            <h2 className="adm-panel-title">
                {isArg ? "Jugadores Argentina 🇦🇷" : "Jugadores World 🌍"}
            </h2>
            <div className="adm-tab-row">
                <button
                    className={`adm-tab ${tab === "add" ? "adm-tab--active" : ""}`}
                    onClick={() => setTab("add")}
                >
                    Agregar
                </button>
                <button
                    className={`adm-tab ${tab === "edit" ? "adm-tab--active" : ""}`}
                    onClick={() => setTab("edit")}
                >
                    Editar / Eliminar
                </button>
            </div>
            <div className="adm-legacy-wrap">
                {tab === "add"
                    ? (isArg ? <AddPlayerFormArg /> : <AddPlayerForm />)
                    : (isArg ? <EditPlayerFormArg /> : <EditPlayerForm />)
                }
            </div>
        </div>
    );
};

// ─── FIELDS PANEL ─────────────────────────────────────────────────────────────
const FieldsPanel = () => (
    <div className="adm-panel">
        <h2 className="adm-panel-title">Administrar Campos</h2>
        <div className="adm-legacy-wrap">
            <ManageFields />
        </div>
    </div>
);

// ─── TRANSFERMARKT DUPLICATES CHECKER ────────────────────────────────────────
const TMDuplicatesChecker = ({ onLoadPlayer }) => {
    const [dupes, setDupes] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [clearing, setClearing] = React.useState({}); // { [playerId]: true }

    const check = async () => {
        setLoading(true);
        try {
            const data = await adminFetch('/players/tm-duplicate-ids');
            setDupes(data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const clearTM = async (playerId, nombre) => {
        if (!window.confirm(`¿Desvincular TM de ${nombre}? Volverá a la cola de enriquecimiento.`)) return;
        setClearing(c => ({ ...c, [playerId]: true }));
        try {
            await adminFetch(`/players/${playerId}/tm-unlink`, { method: 'POST' });
            // Remove player from local state
            setDupes(prev => {
                const newDupes = prev.duplicates.map(d => ({
                    ...d,
                    players: d.players.filter(p => String(p.id) !== String(playerId)),
                    count: d.players.filter(p => String(p.id) !== String(playerId)).length,
                })).filter(d => d.count > 1);
                return { duplicates: newDupes, total: newDupes.length };
            });
        } catch (e) { alert('Error: ' + e.message); }
        finally { setClearing(c => ({ ...c, [playerId]: false })); }
    };

    return (
        <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    🔗 TM IDs duplicados
                </div>
                <button onClick={check} disabled={loading} style={{ background: 'none', border: '1px solid #2d3748', borderRadius: 4, color: '#94a3b8', fontSize: 11, padding: '2px 8px', cursor: 'pointer' }}>
                    {loading ? '⏳' : dupes === null ? 'Verificar' : '↺ Re-verificar'}
                </button>
                {dupes && <span style={{ fontSize: 11, color: dupes.total > 0 ? '#f87171' : '#4ade80' }}>
                    {dupes.total > 0 ? `⚠ ${dupes.total} duplicados encontrados` : '✓ Sin duplicados'}
                </span>}
            </div>
            {dupes?.duplicates?.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
                    {dupes.duplicates.map(d => (
                        <div key={d._id} style={{ background: '#1a0f0f', border: '1px solid #3a1a1a', borderRadius: 6, padding: '10px 12px', fontSize: 11 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                <span style={{ color: '#f87171', fontWeight: 700 }}>TM ID: {d._id}</span>
                                <span style={{ color: '#64748b' }}>({d.count} jugadores)</span>
                                <a href={`https://www.transfermarkt.com/x/profil/spieler/${d._id}`} target="_blank" rel="noreferrer"
                                    style={{ color: '#60a5fa', fontSize: 10, marginLeft: 'auto' }}>Ver en TM ↗</a>
                            </div>
                            {d.players.map((p, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', borderTop: i > 0 ? '1px solid #2d1a1a' : 'none' }}>
                                    <div style={{ flex: 1, color: '#94a3b8' }}>
                                        <span style={{ color: '#e2e8f0' }}>{p.nombre}</span>
                                        <span style={{ color: '#64748b', marginLeft: 6 }}>({p.fecha_nacimiento} · {p.pais})</span>
                                    </div>
                                    <button
                                        onClick={() => onLoadPlayer && onLoadPlayer(p.id)}
                                        style={{ background: '#1e3a5f', border: '1px solid #2563eb', borderRadius: 4, color: '#60a5fa', fontSize: 10, padding: '2px 7px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    >
                                        ✏ Corregir
                                    </button>
                                    <button
                                        onClick={() => clearTM(p.id, p.nombre)}
                                        disabled={clearing[p.id]}
                                        style={{ background: '#3a1a1a', border: '1px solid #7f1d1d', borderRadius: 4, color: '#f87171', fontSize: 10, padding: '2px 7px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    >
                                        {clearing[p.id] ? '⏳' : '✕ Limpiar TM'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── MODAL: RESOLVER DUPLICADOS TM (cards) ───────────────────────────────────
const TMDuplicatesResolverModal = ({ onClose }) => {
    const [dupes, setDupes] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [busy, setBusy] = React.useState({}); // { [playerId]: 'deleting' | 'assigning' }
    const [chosen, setChosen] = React.useState({}); // { [tmId]: playerId } — cuál es el "correcto"
    const [newIdInputs, setNewIdInputs] = React.useState({}); // { [playerId]: "12345 or url" }

    const load = async () => {
        setLoading(true);
        try {
            const data = await adminFetch('/players/tm-duplicate-ids');
            setDupes(data);
            // Pre-seleccionar el primero de cada grupo como "correcto" por defecto
            const initChosen = {};
            (data.duplicates || []).forEach(d => { initChosen[d._id] = String(d.players[0]?.id); });
            setChosen(initChosen);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    React.useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const removeFromState = (tmId, playerId) => {
        setDupes(prev => {
            const newDupes = prev.duplicates.map(d => {
                if (d._id !== tmId) return d;
                const players = d.players.filter(p => String(p.id) !== String(playerId));
                return { ...d, players, count: players.length };
            }).filter(d => d.count > 1);
            return { duplicates: newDupes, total: newDupes.length };
        });
    };

    const handleDelete = async (tmId, p) => {
        if (!window.confirm(`¿Eliminar definitivamente a ${p.nombre}? Esta acción no se puede deshacer.`)) return;
        setBusy(b => ({ ...b, [p.id]: 'deleting' }));
        try {
            const raw = localStorage.getItem('authUserData');
            const token = raw ? JSON.parse(raw).token : null;
            const res = await fetch(`${API}/players/${p.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('Error al eliminar');
            removeFromState(tmId, p.id);
        } catch (e) { alert('Error: ' + e.message); }
        finally { setBusy(b => ({ ...b, [p.id]: null })); }
    };

    const handleAssignNewId = async (tmId, p) => {
        const raw = (newIdInputs[p.id] || '').trim();
        if (!raw) { alert('Ingresá un TM ID o URL de Transfermarkt.'); return; }
        // Aceptar tanto un ID puro como una URL completa
        let newTmId = raw;
        let newTmUrl = null;
        const urlMatch = raw.match(/spieler\/(\d+)/);
        if (urlMatch) {
            newTmId = urlMatch[1];
            newTmUrl = raw.replace('transfermarkt.es', 'transfermarkt.com');
        }
        setBusy(b => ({ ...b, [p.id]: 'assigning' }));
        try {
            await adminFetch(`/players/${p.id}/tm-set-id`, {
                method: 'POST',
                body: JSON.stringify({ tm_id: newTmId, tm_url: newTmUrl }),
            });
            removeFromState(tmId, p.id);
            setNewIdInputs(s => ({ ...s, [p.id]: '' }));
        } catch (e) { alert('Error: ' + e.message); }
        finally { setBusy(b => ({ ...b, [p.id]: null })); }
    };

    const handleUnlink = async (tmId, p) => {
        setBusy(b => ({ ...b, [p.id]: 'unlinking' }));
        try {
            await adminFetch(`/players/${p.id}/tm-unlink`, { method: 'POST' });
            removeFromState(tmId, p.id);
        } catch (e) { alert('Error: ' + e.message); }
        finally { setBusy(b => ({ ...b, [p.id]: null })); }
    };

    return (
        <div style={modalOverlayStyle}>
            <div style={{ ...modalBoxStyle, maxWidth: 920 }}>
                <div style={modalHeaderStyle}>
                    <h3 style={{ margin: 0, fontSize: 16, color: '#e2e8f0' }}>🔗 Resolver TM IDs duplicados</h3>
                    <button onClick={onClose} style={modalCloseBtnStyle}>✕</button>
                </div>
                <div style={{ padding: '14px 20px', overflowY: 'auto', flex: 1 }}>
                    {loading && <p style={{ color: '#94a3b8' }}>Cargando…</p>}
                    {!loading && dupes?.duplicates?.length === 0 && (
                        <p style={{ color: '#4ade80' }}>✓ No hay TM IDs duplicados.</p>
                    )}
                    {!loading && dupes?.duplicates?.map(d => (
                        <div key={d._id} style={{ background: '#16181f', border: '1px solid #2d3748', borderRadius: 8, padding: 14, marginBottom: 14 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                                <span style={{ color: '#f87171', fontWeight: 700, fontSize: 13 }}>TM ID: {d._id}</span>
                                <span style={{ color: '#64748b', fontSize: 12 }}>({d.count} jugadores con este ID)</span>
                                <a href={`https://www.transfermarkt.com/x/profil/spieler/${d._id}`} target="_blank" rel="noreferrer"
                                    style={{ color: '#60a5fa', fontSize: 11, marginLeft: 'auto' }}>Ver en TM ↗</a>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {d.players.map(p => {
                                    const isChosen = String(chosen[d._id]) === String(p.id);
                                    return (
                                        <div key={p.id} style={{
                                            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                                            borderRadius: 6, background: isChosen ? '#0f2a1a' : '#1a1d27',
                                            border: `1px solid ${isChosen ? '#22c55e' : '#2d3748'}`,
                                        }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name={`correct-${d._id}`}
                                                    checked={isChosen}
                                                    onChange={() => setChosen(c => ({ ...c, [d._id]: String(p.id) }))}
                                                />
                                                <span style={{ fontSize: 11, color: isChosen ? '#4ade80' : '#94a3b8' }}>
                                                    {isChosen ? '✓ Correcto' : 'Marcar correcto'}
                                                </span>
                                            </label>
                                            <div style={{ flex: 1, fontSize: 12 }}>
                                                <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{p.nombre}</span>
                                                <span style={{ color: '#64748b', marginLeft: 6 }}>
                                                    {p.fecha_nacimiento} · {p.pais}
                                                </span>
                                            </div>
                                            {!isChosen && (
                                                <>
                                                    <input
                                                        placeholder="Nuevo TM ID o URL"
                                                        value={newIdInputs[p.id] || ''}
                                                        onChange={e => setNewIdInputs(s => ({ ...s, [p.id]: e.target.value }))}
                                                        style={{ background: '#0f1117', border: '1px solid #2d3748', borderRadius: 4, color: '#e2e8f0', fontSize: 11, padding: '4px 8px', width: 150 }}
                                                    />
                                                    <button
                                                        onClick={() => handleAssignNewId(d._id, p)}
                                                        disabled={!!busy[p.id]}
                                                        style={{ background: '#1e3a5f', border: '1px solid #2563eb', borderRadius: 4, color: '#60a5fa', fontSize: 10, padding: '4px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                    >
                                                        {busy[p.id] === 'assigning' ? '⏳' : '✓ Asignar nuevo ID'}
                                                    </button>
                                                    <button
                                                        onClick={() => handleUnlink(d._id, p)}
                                                        disabled={!!busy[p.id]}
                                                        style={{ background: '#2d2410', border: '1px solid #92660a', borderRadius: 4, color: '#fbbf24', fontSize: 10, padding: '4px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                    >
                                                        {busy[p.id] === 'unlinking' ? '⏳' : '↺ Desvincular'}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(d._id, p)}
                                                        disabled={!!busy[p.id]}
                                                        style={{ background: '#3a1a1a', border: '1px solid #7f1d1d', borderRadius: 4, color: '#f87171', fontSize: 10, padding: '4px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                    >
                                                        {busy[p.id] === 'deleting' ? '⏳' : '🗑 Eliminar'}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ─── MODAL: REVISAR Y ELIMINAR JUGADORES SIN TM ID ───────────────────────────
const TMNoIdReviewModal = ({ onClose }) => {
    const [players, setPlayers] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [keep, setKeep] = React.useState({}); // { [playerId]: true } → no eliminar
    const [filter, setFilter] = React.useState('');
    const [deleting, setDeleting] = React.useState(false);

    React.useEffect(() => {
        adminFetch('/players/tm-no-id-list')
            .then(data => setPlayers(data.players || []))
            .catch(e => console.error(e))
            .finally(() => setLoading(false));
    }, []);

    const filtered = React.useMemo(() => {
        if (!players) return [];
        const f = filter.trim().toLowerCase();
        if (!f) return players;
        return players.filter(p =>
            (p.nombre || '').toLowerCase().includes(f) ||
            (p.clubes || []).some(c => c.toLowerCase().includes(f))
        );
    }, [players, filter]);

    const toggleKeep = (id) => setKeep(k => ({ ...k, [id]: !k[id] }));

    const keepCount = Object.values(keep).filter(Boolean).length;
    const totalCount = players?.length || 0;
    const deleteCount = totalCount - keepCount;

    const handleBulkDelete = async () => {
        const excludeIds = Object.keys(keep).filter(id => keep[id]);
        const msg = `Esto va a ELIMINAR ${deleteCount} jugadores sin TM ID (se conservan ${keepCount} marcados como "no eliminar").\n\n¿Confirmás? Esta acción NO se puede deshacer.`;
        if (!window.confirm(msg)) return;
        setDeleting(true);
        try {
            const data = await adminFetch('/players/tm-bulk-delete-no-id', {
                method: 'POST',
                body: JSON.stringify({ excludeIds }),
            });
            alert(`✓ Se eliminaron ${data.deletedCount} jugadores.`);
            onClose();
        } catch (e) { alert('Error: ' + e.message); }
        finally { setDeleting(false); }
    };

    return (
        <div style={modalOverlayStyle}>
            <div style={{ ...modalBoxStyle, maxWidth: 760 }}>
                <div style={modalHeaderStyle}>
                    <h3 style={{ margin: 0, fontSize: 16, color: '#e2e8f0' }}>📋 Revisar jugadores sin TM ID</h3>
                    <button onClick={onClose} style={modalCloseBtnStyle}>✕</button>
                </div>
                <div style={{ padding: '14px 20px 8px' }}>
                    <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 10px' }}>
                        Tildá <strong>"No eliminar"</strong> en los que querés conservar. El resto se eliminará al confirmar.
                    </p>
                    <input
                        placeholder="Filtrar por nombre o club…"
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                        style={{ width: '100%', background: '#0f1117', border: '1px solid #2d3748', borderRadius: 6, color: '#e2e8f0', fontSize: 13, padding: '8px 12px', marginBottom: 8, boxSizing: 'border-box' }}
                    />
                    {!loading && (
                        <div style={{ display: 'flex', gap: 14, fontSize: 12, marginBottom: 8 }}>
                            <span style={{ color: '#94a3b8' }}>Total: <strong style={{ color: '#e2e8f0' }}>{totalCount}</strong></span>
                            <span style={{ color: '#4ade80' }}>A conservar: <strong>{keepCount}</strong></span>
                            <span style={{ color: '#f87171' }}>A eliminar: <strong>{deleteCount}</strong></span>
                        </div>
                    )}
                </div>
                <div style={{ padding: '0 20px', overflowY: 'auto', flex: 1 }}>
                    {loading && <p style={{ color: '#94a3b8' }}>Cargando…</p>}
                    {!loading && filtered.map(p => (
                        <div key={p._id} style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px',
                            borderRadius: 6, marginBottom: 4,
                            background: keep[p._id] ? '#0f2a1a' : '#1a1d27',
                            border: `1px solid ${keep[p._id] ? '#22c55e' : '#2d3748'}`,
                        }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                <input type="checkbox" checked={!!keep[p._id]} onChange={() => toggleKeep(p._id)} />
                                <span style={{ fontSize: 11, color: keep[p._id] ? '#4ade80' : '#64748b' }}>No eliminar</span>
                            </label>
                            <div style={{ flex: 1, fontSize: 12 }}>
                                <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{p.nombre}</span>
                                {p.clubes?.length > 0 && (
                                    <span style={{ color: '#64748b', marginLeft: 8 }}>
                                        {p.clubes.join(', ')}
                                    </span>
                                )}
                                {p.tm_skipped && <span style={{ color: '#fbbf24', marginLeft: 8, fontSize: 10 }}>⚠ saltado</span>}
                            </div>
                        </div>
                    ))}
                    {!loading && filtered.length === 0 && (
                        <p style={{ color: '#64748b', fontSize: 12 }}>Sin resultados para "{filter}".</p>
                    )}
                </div>
                <div style={{ padding: '14px 20px', borderTop: '1px solid #2d3748', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <button onClick={onClose} className="adm-btn adm-btn-outline">Cancelar</button>
                    <button
                        onClick={handleBulkDelete}
                        disabled={loading || deleting || totalCount === 0}
                        className="adm-btn adm-btn-danger"
                    >
                        {deleting ? 'Eliminando…' : `🗑 Eliminar ${deleteCount} jugadores`}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Estilos compartidos para modales ─────────────────────────────────────────
const modalOverlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const modalBoxStyle = {
    background: '#0f1117', border: '1px solid #2d3748', borderRadius: 10,
    width: '90%', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
};
const modalHeaderStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px', borderBottom: '1px solid #2d3748',
};
const modalCloseBtnStyle = {
    background: 'none', border: '1px solid #2d3748', borderRadius: 6,
    color: '#94a3b8', fontSize: 14, padding: '4px 10px', cursor: 'pointer',
};

// ─── TRANSFERMARKT PANEL ──────────────────────────────────────────────────────
const TransfermarktPanel = () => {
    const [player, setPlayer]           = useState(null);
    const [done, setDone]               = useState(false);
    const [url, setUrl]                 = useState('');
    const [loading, setLoading]         = useState(false);
    const [skipLoading, setSkipLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [msg, setMsg]                 = useState(null);

    // Inline-edit state
    const [editNombre, setEditNombre]   = useState('');
    const [editImg, setEditImg]         = useState('');
    const [editFecha, setEditFecha]     = useState('');
    const [imgPreviewErr, setImgPreviewErr] = useState(false);
    // Duplicate TM ID check
    const [urlDuplicate, setUrlDuplicate] = useState(null); // { nombre, _id } | null

    // Stats
    const [stats, setStats]             = useState(null);

    // Photo filter: 'all' | 'none' | 'with'
    const [imgFilter, setImgFilter]     = useState('all');
    // Birth-year filter (empty string = no filter)
    const [minYear, setMinYear]         = useState('');
    // Show only re-intento players (tm_id exists but fichajes empty)
    const [retryOnly, setRetryOnly]     = useState(false);

    // Ref to trigger auto-enrich when a retry player loads
    const autoEnrichRef = useRef(false);
    const [autoSearchStatus, setAutoSearchStatus] = useState(null); // 'searching' | 'found' | 'not_found'
    // Lock para evitar operaciones concurrentes
    const processingRef = useRef(false);
    // ID del jugador actualmente en pantalla (para validar que no cambió mid-async)
    const currentPlayerIdRef = useRef(null);
    // Ref al handleEnrich más reciente (evita stale closure en triggerAutoSearch)
    const handleEnrichRef = useRef(null);

    // Modales de gestión masiva de TM IDs
    const [showDupesResolver, setShowDupesResolver] = useState(false);
    const [showNoIdReview, setShowNoIdReview] = useState(false);

    const fetchStats = useCallback(async (overrideImg, overrideYear, overrideRetry) => {
        try {
            const f = overrideImg   !== undefined ? overrideImg   : imgFilter;
            const y = overrideYear  !== undefined ? overrideYear  : minYear;
            const r = overrideRetry !== undefined ? overrideRetry : retryOnly;
            const params = new URLSearchParams();
            if (r) {
                params.set('retry', 'true');
            } else {
                if (f && f !== 'all')    params.set('img', f);
                if (y && y.length === 4) params.set('minYear', y);
            }
            const qs = params.toString() ? `?${params.toString()}` : '';
            const data = await adminFetch(`/players/tm-stats${qs}`);
            setStats(data);
        } catch { /* ignore */ }
    }, [imgFilter, minYear, retryOnly]);

    const fetchNext = useCallback(async (overrideImg, overrideYear, overrideRetry) => {
        // Reset flags BEFORE any setState so the effects can't fire mid-fetch
        autoEnrichRef.current = false;
        processingRef.current = false;
        currentPlayerIdRef.current = null;
        setAutoSearchStatus(null);
        setMsg(null);
        setUrl('');
        setConfirmDelete(false);
        setEditNombre('');
        setEditImg('');
        setEditFecha('');
        setUrlDuplicate(null);
        setImgPreviewErr(false);
        setLoading(true);
        try {
            const f = overrideImg   !== undefined ? overrideImg   : imgFilter;
            const y = overrideYear  !== undefined ? overrideYear  : minYear;
            const r = overrideRetry !== undefined ? overrideRetry : retryOnly;
            const params = new URLSearchParams();
            if (r) {
                params.set('retry', 'true');
            } else {
                if (f && f !== 'all')    params.set('img', f);
                if (y && y.length === 4) params.set('minYear', y);
            }
            const qs = params.toString() ? `?${params.toString()}` : '';
            const data = await adminFetch(`/players/tm-next${qs}`);
            if (data.done) {
                setDone(true);
                setPlayer(null);
            } else {
                // Set auto-enrich flag BEFORE the setState calls that trigger the effect,
                // otherwise React 17 runs the effect before this assignment is reached
                if (data.player.tm_id && data.player.tm_url) {
                    autoEnrichRef.current = true;
                }
                currentPlayerIdRef.current = String(data.player._id);
                setPlayer(data.player);
                setDone(false);
                setEditNombre(data.player.nombre || '');
                setEditImg(data.player.img || '');
                setEditFecha(data.player.fecha_nacimiento || '');
                setUrlDuplicate(null);
                if (data.player.tm_url) setUrl(data.player.tm_url);
                // Auto-copy name to clipboard for quick TM search
                const clipboardText = `${data.player.nombre} transfermarkt ${data.player.fecha_nacimiento || ""}`.trim();
                navigator.clipboard?.writeText(clipboardText).catch(() => {});
                // Auto-search deshabilitado — usar el botón "Buscar en TM"
            }
        } catch (e) {
            setMsg({ type: 'error', text: e.message });
        } finally {
            setLoading(false);
        }
    }, [imgFilter, minYear, retryOnly]);

    // Load a specific player by ID (used from duplicates fixer)
    const loadPlayerById = useCallback(async (playerId) => {
        autoEnrichRef.current = false;
        setAutoSearchStatus(null);
        setMsg(null); setUrl(''); setConfirmDelete(false);
        setEditNombre(''); setEditImg(''); setEditFecha(''); setUrlDuplicate(null); setImgPreviewErr(false);
        setLoading(true);
        try {
            const data = await adminFetch(`/players/${playerId}/tm-load`);
            if (data.player) {
                setPlayer(data.player);
                setDone(false);
                setEditNombre(data.player.nombre || '');
                setEditImg(data.player.img || '');
                setEditFecha(data.player.fecha_nacimiento || '');
                if (data.player.tm_url) setUrl(data.player.tm_url);
            }
        } catch (e) { setMsg({ type: 'error', text: e.message }); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        fetchNext();
        fetchStats();
    }, [fetchNext, fetchStats]);

    // Auto-enrich when a re-intento player loads (has tm_id + tm_url)
    useEffect(() => {
        if (!autoEnrichRef.current) return;
        if (!player?.tm_id || !url) return;
        autoEnrichRef.current = false;
        const t = setTimeout(() => handleEnrich(), 700); // eslint-disable-line react-hooks/exhaustive-deps
        return () => clearTimeout(t);
    }, [player, url]); // eslint-disable-line react-hooks/exhaustive-deps

    const triggerAutoSearch = (playerId) => {
        // Guard: si ya hay algo procesando, no arrancar otra búsqueda
        if (processingRef.current) return;
        processingRef.current = true;
        setAutoSearchStatus('searching');

        adminFetch(`/players/${playerId}/tm-auto-search`, { method: 'POST' })
            .then(data => {
                // Verificar que el jugador no cambió mientras esperábamos
                if (currentPlayerIdRef.current !== String(playerId)) {
                    processingRef.current = false;
                    return;
                }
                if (data.found) {
                    setUrl(data.tmUrl);
                    setAutoSearchStatus('found');
                    // Delay de 2s antes de enriquecer para que todo esté estable
                    setTimeout(() => {
                        if (currentPlayerIdRef.current !== String(playerId)) {
                            processingRef.current = false;
                            return;
                        }
                        handleEnrichRef.current?.(data.tmUrl);
                    }, 2000);
                } else {
                    processingRef.current = false;
                    setAutoSearchStatus('not_found');
                }
            })
            .catch(() => {
                processingRef.current = false;
                setAutoSearchStatus('not_found');
            });
    };

    const handleEnrich = async (overrideUrl) => {
        // Guard: si se llama desde un onClick directo, overrideUrl puede ser el SyntheticEvent, no un string
        const safeOverride = typeof overrideUrl === 'string' ? overrideUrl : null;
        const enrichUrl = safeOverride || url;
        if (!enrichUrl || !enrichUrl.trim() || !player) return;
        setLoading(true);
        setMsg(null);
        const isRetry = !!player.tm_id; // already has tm_id but incomplete data
        try {
            // 1. Save nombre/img changes first (silently, if any)
            await patchBasicIfChanged();
            // 2. Scrape TM with a 40 s timeout so it never hangs forever
            const controller = new AbortController();
            const tmTimeout = setTimeout(() => controller.abort(), 40_000);
            let data;
            try {
                const raw = localStorage.getItem('authUserData');
                const token = raw ? JSON.parse(raw).token : null;
                const res = await fetch(`${API}/players/${player._id}/tm-enrich`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ url: enrichUrl.trim() }),
                    signal: controller.signal,
                });
                clearTimeout(tmTimeout);
                data = await res.json();
                if (!res.ok) throw new Error(data.message || 'Error al enriquecer');
            } catch (fetchErr) {
                clearTimeout(tmTimeout);
                // If this was a re-intento and it failed again → auto-skip and advance
                if (isRetry) {
                    await adminFetch(`/players/${player._id}/tm-skip`, { method: 'POST' });
                    setMsg({ type: 'info', text: `⚠ ${player.nombre}: falló de nuevo → saltado automáticamente.` });
                    fetchStats();
                    processingRef.current = false;
                    setTimeout(fetchNext, 2000);
                    return;
                }
                throw fetchErr;
            }
            setMsg({
                type: 'success',
                text: `✓ ${editNombre || player.nombre} enriquecido — ${data.fichajes} fichajes, ${data.logros_detallados} logros, ${data.palmares} palmarés`,
            });
            fetchStats();
            processingRef.current = false;
            setTimeout(fetchNext, 2000);
        } catch (e) {
            processingRef.current = false;
            setMsg({ type: 'error', text: e.message });
        } finally {
            setLoading(false);
        }
    };

    handleEnrichRef.current = handleEnrich;

    const handleSkip = async () => {
        if (!player) return;
        setSkipLoading(true);
        setMsg(null);
        try {
            await adminFetch(`/players/${player._id}/tm-skip`, { method: 'POST' });
            setMsg({ type: 'info', text: `${player.nombre} marcado como saltado.` });
            fetchStats();
            setTimeout(fetchNext, 2000);
        } catch (e) {
            setMsg({ type: 'error', text: e.message });
        } finally {
            setSkipLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!player) return;
        setDeleteLoading(true);
        setMsg(null);
        try {
            await adminFetch(`/players/${player._id}`, { method: 'DELETE' });
            setMsg({ type: 'success', text: `🗑 ${player.nombre} eliminado.` });
            setConfirmDelete(false);
            fetchStats();
            setTimeout(fetchNext, 2000);
        } catch (e) {
            setMsg({ type: 'error', text: e.message });
        } finally {
            setDeleteLoading(false);
        }
    };

    // Check if a TM URL's ID is already used by another player
    const checkUrlDuplicate = async (urlValue) => {
        const match = urlValue.match(/spieler\/(\d+)/);
        if (!match) { setUrlDuplicate(null); return; }
        const tmId = match[1];
        try {
            const data = await adminFetch(`/players/tm-check-bulk?tmIds=${tmId}`);
            const entry = data.existing?.[tmId];
            if (entry && entry.id !== player?._id) {
                setUrlDuplicate({ nombre: entry.nombre, _id: entry.id });
            } else {
                setUrlDuplicate(null);
            }
        } catch { setUrlDuplicate(null); }
    };

    const handleUrlChange = (v) => {
        setUrl(v);
        setUrlDuplicate(null);
        if (v.includes('spieler/')) checkUrlDuplicate(v);
    };

    // Patch nombre/img/fecha if they changed — called from handleEnrich before scraping
    const patchBasicIfChanged = async () => {
        const body = {};
        if (editNombre.trim() && editNombre.trim() !== player.nombre) body.nombre = editNombre.trim();
        if (editImg.trim() !== (player.img || '')) body.img = editImg.trim();
        if (editFecha.trim() && editFecha.trim() !== player.fecha_nacimiento) body.fecha_nacimiento = editFecha.trim();
        if (Object.keys(body).length === 0) return; // nothing to patch
        const data = await adminFetch(`/players/${player._id}/tm-edit`, {
            method: 'PATCH',
            body: JSON.stringify(body),
        });
        setPlayer(prev => ({ ...prev, ...data.player }));
    };

    // Progress: withTmId out of total (straightforward %)
    const pct = stats
        ? Math.round((stats.withTmId / Math.max(stats.total, 1)) * 100)
        : 0;

    return (
        <div className="adm-panel">
            <h2 className="adm-panel-title">Transfermarkt — Extra Info</h2>
            <p className="adm-panel-desc">
                Enriquecé jugadores world con datos de Transfermarkt: fichajes, historial de valores, palmarés y logros detallados.
                Se procesa de a uno, los que ya tienen <code>tm_id</code> o fueron saltados se omiten.
            </p>

            {/* ── Stats banner ── */}
            {stats && (
                <div className="adm-tm-stats">
                    <div className="adm-tm-stat adm-tm-stat--done">
                        <span className="adm-tm-stat-val">{stats.withTmId}</span>
                        <span className="adm-tm-stat-lbl">con TM ID</span>
                    </div>
                    <div className="adm-tm-stat adm-tm-stat--pending">
                        <span className="adm-tm-stat-val">{stats.pending}</span>
                        <span className="adm-tm-stat-lbl">pendientes</span>
                    </div>
                    {(imgFilter !== 'all' || (minYear && minYear.length === 4)) && (
                        <div className="adm-tm-stat adm-tm-stat--filtered">
                            <span className="adm-tm-stat-val">{stats.filteredPending ?? '—'}</span>
                            <span className="adm-tm-stat-lbl">pend. filtro</span>
                        </div>
                    )}
                    <div className="adm-tm-stat adm-tm-stat--skipped">
                        <span className="adm-tm-stat-val">{stats.skippedCount}</span>
                        <span className="adm-tm-stat-lbl">saltados</span>
                    </div>
                    <div
                        className={`adm-tm-stat adm-tm-stat--noimg${imgFilter === 'none' ? ' adm-tm-stat--active' : ''}`}
                        title="Jugadores sin foto — clic para filtrar"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            const next = imgFilter === 'none' ? 'all' : 'none';
                            setImgFilter(next);
                            setRetryOnly(false);
                            setDone(false);
                            fetchNext(next, minYear, false);
                            fetchStats(next, minYear, false);
                        }}
                    >
                        <span className="adm-tm-stat-val">{stats.noImg ?? '—'}</span>
                        <span className="adm-tm-stat-lbl">sin foto</span>
                    </div>
                    <div className="adm-tm-stat adm-tm-stat--total">
                        <span className="adm-tm-stat-val">{stats.total}</span>
                        <span className="adm-tm-stat-lbl">total</span>
                    </div>
                    <div className="adm-tm-progress-wrap">
                        <div className="adm-tm-progress-bar">
                            <div className="adm-tm-progress-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="adm-tm-progress-pct">{pct}%</span>
                    </div>
                </div>
            )}

            {/* ── Duplicate TM IDs checker ── */}
            <TMDuplicatesChecker onLoadPlayer={loadPlayerById} />

            {/* ── Accesos a herramientas de limpieza masiva ── */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <button
                    className="adm-btn adm-btn-outline"
                    onClick={() => setShowDupesResolver(true)}
                    style={{ fontSize: 12 }}
                >
                    🔗 Resolver duplicados (cards)
                </button>
                <button
                    className="adm-btn adm-btn-outline"
                    onClick={() => setShowNoIdReview(true)}
                    style={{ fontSize: 12 }}
                >
                    📋 Revisar y limpiar sin TM ID
                </button>
            </div>
            {showDupesResolver && <TMDuplicatesResolverModal onClose={() => setShowDupesResolver(false)} />}
            {showNoIdReview && <TMNoIdReviewModal onClose={() => setShowNoIdReview(false)} />}

            {/* ── Filters row ── */}
            <div className="adm-tm-filters-wrap">
                <div className="adm-tm-filter-row">
                    <span className="adm-tm-filter-label">Foto:</span>
                    {[
                        { key: 'all',  label: '🌐 Todos' },
                        { key: 'none', label: '❌ Sin foto' },
                        { key: 'with', label: '✅ Con foto' },
                    ].map(opt => (
                        <button
                            key={opt.key}
                            className={`adm-tm-filter-btn${imgFilter === opt.key ? ' adm-tm-filter-btn--active' : ''}`}
                            onClick={() => {
                                if (imgFilter === opt.key && !retryOnly) return;
                                setImgFilter(opt.key);
                                setRetryOnly(false);
                                setDone(false);
                                fetchNext(opt.key, minYear, false);
                                fetchStats(opt.key, minYear, false);
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>

                <div className="adm-tm-filter-row">
                    <span className="adm-tm-filter-label">Tipo:</span>
                    <button
                        className={`adm-tm-filter-btn adm-tm-filter-btn--retry${retryOnly ? ' adm-tm-filter-btn--active' : ''}`}
                        onClick={() => {
                            const next = !retryOnly;
                            setRetryOnly(next);
                            setDone(false);
                            fetchNext(imgFilter, minYear, next);
                            fetchStats(imgFilter, minYear, next);
                        }}
                    >
                        ⚠ Solo re-intento
                    </button>
                </div>

                <div className="adm-tm-filter-row">
                    <span className="adm-tm-filter-label">Año mín.:</span>
                    <input
                        className="adm-tm-year-input"
                        type="number"
                        min="1940"
                        max={new Date().getFullYear()}
                        placeholder="ej: 1985"
                        value={minYear}
                        onChange={e => {
                            const val = e.target.value;
                            setMinYear(val);
                            // Apply immediately once we have a 4-digit year or it's cleared
                            if (val === '' || val.length === 4) {
                                setDone(false);
                                fetchNext(imgFilter, val);
                                fetchStats(imgFilter, val);
                            }
                        }}
                    />
                    {minYear && (
                        <button
                            className="adm-tm-filter-btn"
                            onClick={() => {
                                setMinYear('');
                                setDone(false);
                                fetchNext(imgFilter, '');
                                fetchStats(imgFilter, '');
                            }}
                        >
                            ✕ Quitar
                        </button>
                    )}
                </div>
            </div>

            {loading && !player && (
                <p className="adm-msg adm-msg-info">Cargando siguiente jugador…</p>
            )}

            {done && (
                <div className="adm-tm-done">
                    <span>🎉</span>
                    <p>No hay más jugadores con ese filtro.</p>
                </div>
            )}

            {player && (
                <div className="adm-tm-card">

                    {/* ── Player header ── */}
                    <div className="adm-tm-player-header">
                        <div className="adm-tm-avatar-wrap">
                            {editImg && !imgPreviewErr ? (
                                <img
                                    src={editImg}
                                    alt={editNombre}
                                    className="adm-tm-avatar"
                                    onError={() => setImgPreviewErr(true)}
                                />
                            ) : (
                                <div className="adm-tm-avatar adm-tm-avatar--placeholder">
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
                                        <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
                                    </svg>
                                </div>
                            )}
                        </div>
                        <div className="adm-tm-player-info">
                            <h3 className="adm-tm-player-name">
                                {player.nombre}
                                {player.tm_id && (
                                    <span className="adm-tm-retry-badge">⚠ re-intento</span>
                                )}
                            </h3>
                            <div className="adm-tm-player-meta">
                                <span>{player.pais}</span>
                                <span>·</span>
                                <span>{player.posicion}</span>
                                <span>·</span>
                                <span>{player.fecha_nacimiento}</span>
                            </div>
                            <div className="adm-mono adm-tm-player-id">{player._id}</div>
                        </div>
                    </div>

                    {/* ── Inline edit: nombre + imagen + fecha (se guardan junto con el enrich) ── */}
                    <div className="adm-tm-edit-section">
                        <div className="adm-tm-edit-label">✏️ Edición rápida — se guarda al extraer</div>
                        <div className="adm-tm-edit-fields">
                            <div className="adm-tm-edit-field">
                                <label>Nombre</label>
                                <input
                                    className="adm-tm-url-input"
                                    value={editNombre}
                                    onChange={e => setEditNombre(e.target.value)}
                                    placeholder="Nombre del jugador"
                                />
                            </div>
                            <div className="adm-tm-edit-field">
                                <label>Fecha nacimiento (DD.MM.YYYY)</label>
                                <input
                                    className="adm-tm-url-input"
                                    value={editFecha}
                                    onChange={e => setEditFecha(e.target.value)}
                                    placeholder="DD.MM.YYYY"
                                    style={{ fontFamily: 'monospace' }}
                                />
                            </div>
                            <div className="adm-tm-edit-field">
                                <label>URL imagen (img)</label>
                                <input
                                    className="adm-tm-url-input"
                                    value={editImg}
                                    onChange={e => { setEditImg(e.target.value); setImgPreviewErr(false); }}
                                    placeholder="https://img.sofascore.com/api/v1/player/.../image"
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Auto-search status + botón ── */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        {!autoSearchStatus || autoSearchStatus === 'not_found' ? (
                            <button
                                className="adm-btn adm-btn-outline"
                                style={{ fontSize: 12, padding: '4px 12px' }}
                                onClick={() => triggerAutoSearch(player._id)}
                                disabled={loading}
                            >
                                🔍 Buscar en TM
                            </button>
                        ) : null}
                        {autoSearchStatus === 'searching' && (
                            <span style={{ fontSize: 12, color: '#94a3b8' }}>🔍 Buscando en Transfermarkt…</span>
                        )}
                        {autoSearchStatus === 'found' && (
                            <span style={{ fontSize: 12, color: '#4ade80' }}>✓ Match encontrado — extrayendo automáticamente…</span>
                        )}
                        {autoSearchStatus === 'not_found' && (
                            <span style={{ fontSize: 12, color: '#f87171' }}>✗ No se encontró match — ingresá la URL manualmente.</span>
                        )}
                    </div>
                    {/* ── TM URL input + duplicate warning ── */}
                    <div className="adm-tm-url-row">
                        <input
                            className="adm-tm-url-input"
                            value={url}
                            onChange={e => handleUrlChange(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && !urlDuplicate && handleEnrich()}
                            placeholder="https://www.transfermarkt.com/.../profil/spieler/XXXXX"
                            style={urlDuplicate ? { borderColor: '#f87171' } : undefined}
                        />
                    </div>
                    {urlDuplicate && (
                        <div style={{ background: '#3a1a1a', border: '1px solid #f87171', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#f87171', marginBottom: 8 }}>
                            ⚠ Este TM ID ya está vinculado a <strong style={{ color: '#fca5a5' }}>{urlDuplicate.nombre}</strong>.
                            Si continuás, uno de los dos quedará mal vinculado.
                        </div>
                    )}

                    {/* Clubs */}
                    {player.clubes && player.clubes.length > 0 && (
                        <div className="adm-tm-clubs">
                            <div className="adm-tm-clubs-label">Clubes registrados</div>
                            <div className="adm-tm-clubs-list">
                                {player.clubes.map((c, i) => (
                                    <span key={i} className="adm-tm-club-chip">{c}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="adm-tm-actions">
                        <button
                            className="adm-btn adm-btn-primary"
                            onClick={() => handleEnrich()}
                            disabled={loading || !url.trim()}
                        >
                            {loading ? 'Extrayendo…' : '⬇ Extraer y guardar'}
                        </button>
                        <button
                            className="adm-btn adm-btn-outline"
                            onClick={handleSkip}
                            disabled={skipLoading || loading}
                        >
                            {skipLoading ? 'Salteando…' : 'Saltear →'}
                        </button>
                        {!confirmDelete ? (
                            <button
                                className="adm-btn adm-btn-danger"
                                onClick={() => setConfirmDelete(true)}
                                disabled={loading || skipLoading}
                            >
                                🗑 Eliminar
                            </button>
                        ) : (
                            <div className="adm-tm-confirm-delete">
                                <span>¿Seguro?</span>
                                <button
                                    className="adm-btn adm-btn-danger"
                                    onClick={handleDelete}
                                    disabled={deleteLoading}
                                >
                                    {deleteLoading ? 'Eliminando…' : 'Sí, eliminar'}
                                </button>
                                <button
                                    className="adm-btn adm-btn-outline"
                                    onClick={() => setConfirmDelete(false)}
                                >
                                    Cancelar
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {msg && <p className={`adm-msg adm-msg-${msg.type}`}>{msg.text}</p>}
        </div>
    );
};

// ─── EXTENSION TOKEN COPY BUTTON ──────────────────────────────────────────────
const ExtensionTokenButton = () => {
    const [copied, setCopied] = React.useState(false);
    const handleCopy = () => {
        try {
            const raw = localStorage.getItem('authUserData');
            const token = raw ? JSON.parse(raw).token : null;
            if (!token) { alert('No se encontró el token. Iniciá sesión primero.'); return; }
            navigator.clipboard.writeText(token).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2500);
            });
        } catch { alert('Error al copiar el token.'); }
    };
    return (
        <button
            onClick={handleCopy}
            title="Copia el token para pegarlo en la extensión de Chrome"
            style={{
                position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000,
                background: copied ? '#1a3a2a' : '#1a2035',
                color: copied ? '#4ade80' : '#94a3b8',
                border: `1px solid ${copied ? '#4ade80' : '#2d3748'}`,
                borderRadius: '8px', padding: '8px 14px',
                fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px',
            }}
        >
            {copied ? '✓ Token copiado' : '🔑 Copiar token extensión'}
        </button>
    );
};

// ─── NAV ITEMS ────────────────────────────────────────────────────────────────
// ─── AUDIT PANEL ─────────────────────────────────────────────────────────────
const LOG_COLORS = { ok: '#4ade80', mismatch: '#fbbf24', fixed: '#60a5fa', unlinked: '#f87171', failed: '#94a3b8', system: '#a78bfa' };

const AuditPanel = () => {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchStatus = useCallback(async () => {
        try {
            const data = await adminFetch('/players/tm-audit-status');
            setStatus(data);
        } catch (e) {
            console.error('[AuditPanel]', e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
        const interval = setInterval(fetchStatus, 15_000);
        return () => clearInterval(interval);
    }, [fetchStatus]);

    const [fastPassLoading, setFastPassLoading] = React.useState(false);
    const [mismatches, setMismatches] = React.useState(null);
    const [mismatchLoading, setMismatchLoading] = React.useState(false);

    const fetchMismatches = async () => {
        setMismatchLoading(true);
        try {
            const data = await adminFetch('/players/tm-mismatches');
            setMismatches(data.mismatches || []);
        } catch (e) {
            console.error(e);
        } finally {
            setMismatchLoading(false);
        }
    };

    const handleUnlink = async (id, nombre) => {
        if (!window.confirm(`¿Desvincular a ${nombre} de TM?`)) return;
        await adminFetch(`/players/${id}/tm-unlink`, { method: 'POST' });
        fetchMismatches();
        fetchStatus();
    };

    const handleToggle = async () => {
        if (!status) return;
        const path = status.active ? '/players/tm-audit-pause' : '/players/tm-audit-resume';
        await adminFetch(path, { method: 'POST' });
        fetchStatus();
    };

    const handleFastPass = async () => {
        if (fastPassLoading) return;
        setFastPassLoading(true);
        try {
            await adminFetch('/players/tm-audit-fast-pass', { method: 'POST' });
            fetchStatus();
        } finally {
            setFastPassLoading(false);
        }
    };

    if (loading) return <div className="adm-panel"><p style={{ color: '#94a3b8' }}>Cargando…</p></div>;

    const s = status?.stats || {};
    const log = status?.log || [];
    const isActive = status?.active;
    const speedPerHour = Math.round(3600000 / (status?.intervalMs || 20000));

    return (
        <div className="adm-panel">
            <h2 className="adm-panel-title">🔍 Auditoría de vínculos TM</h2>
            <p className="adm-panel-desc">
                Verifica la fecha de nacimiento de cada jugador vinculado a TM.
                <strong style={{ color: '#D4A845' }}> Paso 1:</strong> Fast-pass (sin requests TM) — verifica por overlap de clubes en segundos.
                <strong style={{ color: '#D4A845' }}> Paso 2:</strong> Job automático (1 jugador / {status?.intervalMs ? status.intervalMs/1000 : 20}s, ~{speedPerHour}/hora) para los restantes. Se refresca cada 15s.
            </p>

            {/* Fast Pass */}
            {!status?.fastPassDone && (
                <div style={{ background: '#1a2035', borderRadius: 8, padding: '10px 12px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, fontSize: 12, color: '#94a3b8' }}>
                        ⚡ <strong style={{ color: '#D4A845' }}>Fast-pass no ejecutado.</strong> Verificá rápidamente jugadores por overlap de clubes (sin requests TM).
                        Recomendado antes de activar el job.
                    </div>
                    <button
                        onClick={handleFastPass}
                        disabled={fastPassLoading}
                        className="adm-tm-btn"
                        style={{ background: '#2a1f0a', color: '#D4A845', border: '1px solid #D4A845', minWidth: 110, opacity: fastPassLoading ? 0.7 : 1 }}
                    >
                        {fastPassLoading ? '⏳ Corriendo…' : '⚡ Fast-pass'}
                    </button>
                </div>
            )}
            {status?.fastPassDone && (
                <div style={{ background: '#0a2a1a', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#4ade80' }}>
                    ✓ Fast-pass completado
                    <button onClick={handleFastPass} disabled={fastPassLoading} style={{ marginLeft: 12, background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 11 }}>
                        {fastPassLoading ? '⏳' : '↺ re-ejecutar'}
                    </button>
                </div>
            )}

            {/* Stats */}
            <div className="adm-tm-stats" style={{ marginBottom: 16 }}>
                {[
                    { val: s.pending ?? '—', lbl: 'Pendientes',    cls: '' },
                    { val: s.verified,       lbl: 'Verificados ✓', cls: 'adm-tm-stat--done' },
                    { val: s.mismatch,       lbl: 'Discrepancias', cls: 'adm-tm-stat--pending' },
                    { val: s.autoFixed,      lbl: 'Auto-correg.',  cls: 'adm-tm-stat--done' },
                    { val: s.unlinked,       lbl: 'Desvinculados', cls: 'adm-tm-stat--skipped' },
                    { val: s.failed,         lbl: 'Fallidos',      cls: 'adm-tm-stat--skipped' },
                ].map(({ val, lbl, cls }) => (
                    <div key={lbl} className={`adm-tm-stat ${cls}`}>
                        <span className="adm-tm-stat-val">{val ?? 0}</span>
                        <span className="adm-tm-stat-lbl">{lbl}</span>
                    </div>
                ))}
            </div>

            {/* Estado actual + controles */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1, background: '#1a2035', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
                    <span style={{ color: isActive ? '#4ade80' : '#f87171', fontWeight: 700 }}>
                        {isActive ? '● Activo' : '● Pausado'}
                    </span>
                    {status?.currentPlayer && (
                        <span style={{ color: '#94a3b8', marginLeft: 8 }}>
                            Procesando: <strong style={{ color: '#e2e8f0' }}>{status.currentPlayer}</strong>
                        </span>
                    )}
                    {status?.lastRunAt && !status?.currentPlayer && (
                        <span style={{ color: '#64748b', marginLeft: 8, fontSize: 11 }}>
                            Último: {new Date(status.lastRunAt).toLocaleTimeString('es-AR')}
                        </span>
                    )}
                    {s.pending > 0 && !isActive && (
                        <span style={{ color: '#64748b', marginLeft: 8, fontSize: 11 }}>
                            ~{Math.round(s.pending * (status?.intervalMs || 20000) / 3600000)}h restantes
                        </span>
                    )}
                </div>
                <button
                    onClick={handleToggle}
                    className="adm-tm-btn"
                    style={{ background: isActive ? '#3a1a1a' : '#1a3a2a', color: isActive ? '#f87171' : '#4ade80', minWidth: 90 }}
                >
                    {isActive ? '⏸ Pausar' : '▶ Reanudar'}
                </button>
            </div>

            {/* Mismatches */}
            {s.mismatch > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#f87171', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            ⚠ Discrepancias ({s.mismatch})
                        </div>
                        <button
                            onClick={fetchMismatches}
                            disabled={mismatchLoading}
                            style={{ background: 'none', border: '1px solid #2d3748', borderRadius: 4, color: '#94a3b8', fontSize: 11, padding: '2px 8px', cursor: 'pointer' }}
                        >
                            {mismatchLoading ? '⏳' : mismatches === null ? 'Ver lista' : '↺ Refrescar'}
                        </button>
                    </div>
                    {mismatches !== null && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto' }}>
                            {mismatches.length === 0 && <span style={{ color: '#64748b', fontSize: 12 }}>Sin discrepancias activas.</span>}
                            {mismatches.map(p => (
                                <div key={p._id} style={{ background: '#1a0f0f', border: '1px solid #3a1a1a', borderRadius: 6, padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.nombre}</div>
                                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                                            <span style={{ color: '#4ade80' }}>DB: {p.fecha_nacimiento}</span>
                                            <span style={{ color: '#4a5568', margin: '0 4px' }}>→</span>
                                            <span style={{ color: '#f87171' }}>TM: {p.tm_dob_found}</span>
                                        </div>
                                        <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                                            {p.pais} · {p.posicion}
                                            {p.tm_url && <a href={p.tm_url} target="_blank" rel="noreferrer" style={{ color: '#D4A845', marginLeft: 6 }}>Ver en TM ↗</a>}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleUnlink(p._id, p.nombre)}
                                        style={{ background: '#3a1a1a', border: 'none', borderRadius: 4, color: '#f87171', fontSize: 11, padding: '4px 8px', cursor: 'pointer', flexShrink: 0 }}
                                    >
                                        Desvincular
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Log */}
            <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                    Log reciente
                </div>
                <div style={{ background: '#0a0f1a', borderRadius: 8, padding: '8px 10px', maxHeight: 320, overflowY: 'auto', fontFamily: 'monospace', fontSize: 11, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {log.length === 0 && <span style={{ color: '#4a5568' }}>Sin actividad aún…</span>}
                    {log.map((entry, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                            <span style={{ color: '#4a5568', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                {new Date(entry.at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </span>
                            <span style={{ color: LOG_COLORS[entry.type] || '#94a3b8', whiteSpace: 'nowrap', flexShrink: 0, width: 64 }}>
                                [{entry.type}]
                            </span>
                            <span style={{ color: '#cbd5e1' }}>{entry.msg}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ─── LEAGUES PANEL (modo carrera) ─────────────────────────────────────────────
const STAR_OPTIONS = [];
for (let s = 0.5; s <= 5; s += 0.5) STAR_OPTIONS.push(s);

const LeaguesPanel = () => {
    const [leagues, setLeagues] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [league, setLeague] = useState(null);
    const [loadingLeague, setLoadingLeague] = useState(false);
    const [msg, setMsg] = useState(null);
    const [saving, setSaving] = useState(false);

    // Búsqueda de clubes para agregar
    const [search, setSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);

    const fetchLeagues = useCallback(async () => {
        setLoadingList(true);
        try {
            const data = await adminFetch('/leagues');
            setLeagues(data);
        } catch (e) {
            setMsg({ type: 'error', text: e.message });
        } finally {
            setLoadingList(false);
        }
    }, []);

    useEffect(() => { fetchLeagues(); }, [fetchLeagues]);

    const selectLeague = async (id) => {
        setSelectedId(id);
        setLeague(null);
        setMsg(null);
        setSearch('');
        setSearchResults([]);
        setLoadingLeague(true);
        try {
            const data = await adminFetch(`/leagues/${id}`);
            setLeague(data);
        } catch (e) {
            setMsg({ type: 'error', text: e.message });
        } finally {
            setLoadingLeague(false);
        }
    };

    const handleFieldChange = (field, value) => {
        setLeague(prev => ({ ...prev, [field]: value }));
    };

    const saveSettings = async () => {
        if (!league) return;
        setSaving(true);
        setMsg(null);
        try {
            const body = {
                nombre: league.nombre,
                cupos_champions: Number(league.cupos_champions) || 0,
                cupos_europa: Number(league.cupos_europa) || 0,
                cupos_libertadores: Number(league.cupos_libertadores) || 0,
                cupos_sudamericana: Number(league.cupos_sudamericana) || 0,
                ascensos: Number(league.ascensos) || 0,
                descensos: Number(league.descensos) || 0,
            };
            const data = await adminFetch(`/leagues/${league._id}`, {
                method: 'PUT',
                body: JSON.stringify(body),
            });
            setLeague(prev => ({ ...prev, ...data }));
            setLeagues(prev => prev.map(l => l._id === data._id ? { ...l, ...data } : l));
            setMsg({ type: 'success', text: 'Configuración guardada ✓' });
        } catch (e) {
            setMsg({ type: 'error', text: e.message });
        } finally {
            setSaving(false);
        }
    };

    const saveClubs = async (clubs) => {
        if (!league) return;
        setSaving(true);
        setMsg(null);
        try {
            const data = await adminFetch(`/leagues/${league._id}/clubs`, {
                method: 'PUT',
                body: JSON.stringify({ clubs }),
            });
            setLeague(prev => ({ ...prev, clubs: data.clubs }));
            setLeagues(prev => prev.map(l => l._id === data._id ? { ...l, clubs: data.clubs } : l));
            setMsg({ type: 'success', text: 'Clubes guardados ✓' });
        } catch (e) {
            setMsg({ type: 'error', text: e.message });
        } finally {
            setSaving(false);
        }
    };

    const handleStarsChange = (idx, value) => {
        setLeague(prev => {
            const clubs = prev.clubs.map((c, i) => i === idx ? { ...c, estrellas: Number(value) } : c);
            return { ...prev, clubs };
        });
    };

    const handleNameChange = (idx, value) => {
        setLeague(prev => {
            const clubs = prev.clubs.map((c, i) => i === idx ? { ...c, nombre: value } : c);
            return { ...prev, clubs };
        });
    };

    const handleRemoveClub = (idx) => {
        setLeague(prev => {
            const clubs = prev.clubs.filter((_, i) => i !== idx);
            return { ...prev, clubs };
        });
    };

    const doSearch = async () => {
        if (search.trim().length < 2) return;
        setSearching(true);
        try {
            const data = await adminFetch(`/leagues/clubs/search?q=${encodeURIComponent(search.trim())}`);
            setSearchResults(data);
        } catch (e) {
            setMsg({ type: 'error', text: e.message });
        } finally {
            setSearching(false);
        }
    };

    const handleAddClub = (club) => {
        setLeague(prev => {
            if (prev.clubs.some(c => c.tm_id === club.tm_id)) return prev;
            const estrellas = prev.division === 1 ? 3 : 1.5;
            return { ...prev, clubs: [...prev.clubs, { tm_id: club.tm_id, nombre: club.nombre, estrellas }] };
        });
        setSearchResults(prev => prev.filter(c => c.tm_id !== club.tm_id));
    };

    return (
        <div className="adm-panel" style={{ maxWidth: 1100 }}>
            <h2 className="adm-panel-title">⚽ Ligas — Modo Carrera</h2>
            <p className="adm-panel-desc">
                Editá los clubes (nombre, estrellas, alta/baja) y los cupos de Champions/Europa League, Libertadores/Sudamericana, ascensos y descensos de cada liga. Tocá un nombre para editarlo y después "Guardar clubes".
            </p>

            {msg && <p className={`adm-msg adm-msg-${msg.type}`}>{msg.text}</p>}

            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                {/* Lista de ligas */}
                <div style={{ width: 260, flexShrink: 0, background: '#16181f', border: '1px solid #2d3748', borderRadius: 8, maxHeight: 600, overflowY: 'auto' }}>
                    {loadingList && <p style={{ color: '#94a3b8', padding: 12, fontSize: 12 }}>Cargando…</p>}
                    {!loadingList && leagues.map(l => (
                        <div
                            key={l._id}
                            onClick={() => selectLeague(l._id)}
                            style={{
                                padding: '8px 12px', cursor: 'pointer', fontSize: 12,
                                background: selectedId === l._id ? '#1d4ed8' : 'transparent',
                                color: selectedId === l._id ? '#fff' : '#cbd5e1',
                                borderBottom: '1px solid #2d3748',
                            }}
                        >
                            {l.pais} — {l.nombre} <span style={{ opacity: 0.7 }}>(D{l.division})</span>
                            <div style={{ fontSize: 10, opacity: 0.7 }}>{l.clubs?.length || 0} clubes</div>
                        </div>
                    ))}
                </div>

                {/* Detalle de liga */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {loadingLeague && <p style={{ color: '#94a3b8' }}>Cargando liga…</p>}
                    {!league && !loadingLeague && (
                        <p style={{ color: '#64748b' }}>Seleccioná una liga de la lista.</p>
                    )}
                    {league && !loadingLeague && (
                        <>
                            <h3 style={{ color: '#f8fafc', marginTop: 0 }}>{league.pais} — {league.nombre} (D{league.division})</h3>

                            {/* Configuración de cupos / ascensos / descensos */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                                <div className="adm-field">
                                    <label>Nombre</label>
                                    <input value={league.nombre} onChange={e => handleFieldChange('nombre', e.target.value)} />
                                </div>
                                <div className="adm-field">
                                    <label>Cupos Champions</label>
                                    <input type="number" min="0" value={league.cupos_champions}
                                        onChange={e => handleFieldChange('cupos_champions', e.target.value)} style={{ width: 80 }} />
                                </div>
                                <div className="adm-field">
                                    <label>Cupos Europa League</label>
                                    <input type="number" min="0" value={league.cupos_europa}
                                        onChange={e => handleFieldChange('cupos_europa', e.target.value)} style={{ width: 80 }} />
                                </div>
                                <div className="adm-field">
                                    <label>Cupos Libertadores</label>
                                    <input type="number" min="0" value={league.cupos_libertadores ?? 0}
                                        onChange={e => handleFieldChange('cupos_libertadores', e.target.value)} style={{ width: 80 }} />
                                </div>
                                <div className="adm-field">
                                    <label>Cupos Sudamericana</label>
                                    <input type="number" min="0" value={league.cupos_sudamericana ?? 0}
                                        onChange={e => handleFieldChange('cupos_sudamericana', e.target.value)} style={{ width: 80 }} />
                                </div>
                                <div className="adm-field">
                                    <label>Ascensos</label>
                                    <input type="number" min="0" value={league.ascensos}
                                        onChange={e => handleFieldChange('ascensos', e.target.value)} style={{ width: 80 }} />
                                </div>
                                <div className="adm-field">
                                    <label>Descensos</label>
                                    <input type="number" min="0" value={league.descensos}
                                        onChange={e => handleFieldChange('descensos', e.target.value)} style={{ width: 80 }} />
                                </div>
                            </div>
                            <button className="adm-btn adm-btn-primary" onClick={saveSettings} disabled={saving} style={{ marginBottom: 24 }}>
                                {saving ? 'Guardando…' : 'Guardar configuración'}
                            </button>

                            {/* Búsqueda y agregado de clubes */}
                            <div style={{ marginBottom: 12 }}>
                                <div className="adm-search-row">
                                    <input
                                        placeholder="Buscar club para agregar…"
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && doSearch()}
                                    />
                                    <button className="adm-btn adm-btn-outline" onClick={doSearch} disabled={searching}>
                                        {searching ? '…' : 'Buscar'}
                                    </button>
                                </div>
                                {searchResults.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                                        {searchResults.map(c => (
                                            <button
                                                key={c.tm_id}
                                                onClick={() => handleAddClub(c)}
                                                style={{ background: '#0f2a1a', border: '1px solid #22c55e', borderRadius: 6, color: '#4ade80', fontSize: 11, padding: '4px 8px', cursor: 'pointer' }}
                                            >
                                                + {c.nombre} <span style={{ opacity: 0.6 }}>({c.tm_id})</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Lista de clubes */}
                            <div style={{ background: '#16181f', border: '1px solid #2d3748', borderRadius: 8, maxHeight: 420, overflowY: 'auto' }}>
                                {league.clubs.map((c, idx) => (
                                    <div key={`${c.tm_id}-${idx}`} style={{
                                        display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px',
                                        borderBottom: '1px solid #2d3748', fontSize: 12,
                                    }}>
                                        <input
                                            value={c.nombre}
                                            onChange={e => handleNameChange(idx, e.target.value)}
                                            title="Editar nombre del equipo"
                                            style={{ flex: 1, minWidth: 0, background: '#0f1117', border: '1px solid #2d3748', borderRadius: 4, color: '#e2e8f0', fontSize: 12, padding: '4px 8px' }}
                                        />
                                        {!c.tm_id && <span style={{ color: '#fbbf24', fontSize: 10 }}>⚠ sin tm_id</span>}
                                        <select
                                            value={c.estrellas}
                                            onChange={e => handleStarsChange(idx, e.target.value)}
                                            style={{ background: '#0f1117', border: '1px solid #2d3748', borderRadius: 4, color: '#e2e8f0', fontSize: 11, padding: '2px 6px' }}
                                        >
                                            {STAR_OPTIONS.map(s => <option key={s} value={s}>{'★'.repeat(Math.floor(s))}{s % 1 !== 0 ? '½' : ''} ({s})</option>)}
                                        </select>
                                        <button
                                            onClick={() => handleRemoveClub(idx)}
                                            style={{ background: '#3a1a1a', border: '1px solid #7f1d1d', borderRadius: 4, color: '#f87171', fontSize: 10, padding: '2px 8px', cursor: 'pointer' }}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button className="adm-btn adm-btn-primary" onClick={() => saveClubs(league.clubs)} disabled={saving} style={{ marginTop: 12 }}>
                                {saving ? 'Guardando…' : 'Guardar clubes'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// ─── DEPLOY PANEL ─────────────────────────────────────────────────────────────
const DeployPanel = () => {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);

    const promote = async () => {
        if (!window.confirm('¿Promover staging a producción? Esto hará merge de staging → master.')) return;
        setLoading(true);
        setStatus(null);
        try {
            const raw = localStorage.getItem('authUserData');
            const token = raw ? JSON.parse(raw).token : null;
            const res = await fetch(`${API}/deploy/promote-to-prod`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            setStatus(res.ok ? { ok: true, msg: 'Workflow disparado. El deploy a producción empezará en segundos.' } : { ok: false, msg: data.error });
        } catch (e) {
            setStatus({ ok: false, msg: e.message });
        }
        setLoading(false);
    };

    return (
        <div className="adm-panel">
            <h2>Deploy a Producción</h2>
            <p style={{ color: '#aaa', marginBottom: 24 }}>
                Mergea la rama <strong>staging</strong> a <strong>master</strong> via GitHub Actions.<br />
                Cloudflare buildea y despliega producción automáticamente.
            </p>
            <button className="adm-btn adm-btn-primary" onClick={promote} disabled={loading}
                style={{ background: '#e05252', fontSize: 16, padding: '12px 32px' }}>
                {loading ? 'Disparando...' : '🚀 Promover a Producción'}
            </button>
            {status && (
                <p style={{ marginTop: 16, color: status.ok ? '#4caf50' : '#e05252' }}>
                    {status.msg}
                </p>
            )}
        </div>
    );
};

const IS_STAGING = !!(process.env.REACT_APP_BACKEND_URL?.includes('staging'));

const NAV = [
    { key: "version",       icon: "🔒", label: "Versiones" },
    { key: "users",         icon: "👥", label: "Usuarios" },
    { key: "players_world", icon: "🌍", label: "Jugadores World" },
    { key: "players_arg",   icon: "🇦🇷", label: "Jugadores Arg" },
    { key: "fields",        icon: "📋", label: "Campos" },
    { key: "cotizaciones",  icon: "💰", label: "Cotizaciones" },
    { key: "tm",            icon: "📊", label: "Transfermarkt" },
    { key: "audit",         icon: "🔍", label: "Auditoría TM" },
    { key: "leagues",       icon: "⚽", label: "Ligas (Carrera)" },
    { key: "career_maint",  icon: "🔧", label: "Mantenimiento Carrera" },
    { key: "selecciones",   icon: "🌎", label: "Selecciones (Mundial)" },
    ...(IS_STAGING ? [{ key: "deploy", icon: "🚀", label: "Deploy a Prod" }] : []),
];

// ─── ADMIN PAGE ───────────────────────────────────────────────────────────────
const AdminPage = () => {
    const [active, setActive] = useState("version");
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const renderPanel = () => {
        switch (active) {
            case "version":       return <VersionPanel />;
            case "users":         return <UsersPanel />;
            case "players_world": return <PlayersPanel league="world" />;
            case "players_arg":   return <PlayersPanel league="arg" />;
            case "fields":        return <FieldsPanel />;
            case "cotizaciones":  return <CotizacionesPanel adminFetch={adminFetch} />;
            case "tm":            return <TransfermarktPanel />;
            case "audit":         return <AuditPanel />;
            case "leagues":       return <LeaguesPanel />;
            case "career_maint":  return <CareerMaintenancePanel />;
            case "selecciones":   return <SeleccionesPanel />;
            case "deploy":        return <DeployPanel />;
            default:              return null;
        }
    };

    const handleNav = (key) => {
        setActive(key);
        setSidebarOpen(false);
    };

    return (
        <div className="adm-root">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div className="adm-overlay" onClick={() => setSidebarOpen(false)} />
            )}

            {/* Sidebar */}
            <aside className={`adm-sidebar ${sidebarOpen ? "adm-sidebar--open" : ""}`}>
                <div className="adm-sidebar-header">
                    <span className="adm-sidebar-logo">⚽</span>
                    <span className="adm-sidebar-title">Admin Panel</span>
                </div>
                <nav className="adm-nav">
                    {NAV.map(item => (
                        <button
                            key={item.key}
                            className={`adm-nav-item ${active === item.key ? "adm-nav-item--active" : ""}`}
                            onClick={() => handleNav(item.key)}
                        >
                            <span className="adm-nav-icon">{item.icon}</span>
                            <span className="adm-nav-label">{item.label}</span>
                        </button>
                    ))}
                </nav>
            </aside>

            {/* Main content */}
            <main className="adm-main">
                <div className="adm-topbar">
                    <button className="adm-hamburger" onClick={() => setSidebarOpen(o => !o)}>
                        ☰
                    </button>
                    <span className="adm-topbar-title">
                        {NAV.find(n => n.key === active)?.icon}{" "}
                        {NAV.find(n => n.key === active)?.label}
                    </span>
                </div>
                <div className="adm-content">
                    {renderPanel()}
                </div>
            </main>
            <ExtensionTokenButton />
        </div>
    );
};

export default AdminPage;
